import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../../api/userApi";
import "../../styles/UserManagementPage.css";
import UserAccessControl from "./UserAccessControl";
import { useAuth } from "../../context/AuthContext";

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = currentUser?.role === "admin" || currentUser?.is_superuser;
  const canManageUsers = isAdmin || Boolean(currentUser?.access_rights?.["users.manage"]);
  const [adminTab, setAdminTab] = useState(isAdmin && searchParams.get("tab") === "access" ? "access" : "users");
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [isFormVisible, setIsFormVisible] = useState(false);

  const [form, setForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    role: "qc",
    department: "",
    password: "",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data || []);
    } catch {
      Swal.fire("Error", "Failed to load users", "error");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setEditingUser(null);
    setForm({
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      role: "qc",
      department: "",
      password: "",
    });
    setIsFormVisible(false);
  };

  const buildPayload = () => {
    const payload = {};
    Object.keys(form).forEach((key) => {
      if (key === "password") return;
      if (form[key] !== "" && form[key] !== null) {
        payload[key] = form[key];
      }
    });
    if (form.password?.trim()) {
      payload.password = form.password;
    }
    return payload;
  };

  // ==========================================
  // CONFIRMATION POPUPS
  // ==========================================

  const showCreateConfirmation = async () => {
    const result = await Swal.fire({
      title: "Create New User?",
      width: 480,
      html: `
        <div class="um-confirm-popup">
          <div class="um-confirm-header">
            <div class="um-confirm-icon create">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <path d="M20 8v6M23 11h-6"/>
              </svg>
            </div>
            <p class="um-confirm-subtitle">Review new user details</p>
          </div>
          <div class="um-confirm-details">
            <div class="um-confirm-row">
              <span class="um-confirm-label">Username</span>
              <span class="um-confirm-value">${form.username}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Full Name</span>
              <span class="um-confirm-value">${form.first_name} ${form.last_name}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Email</span>
              <span class="um-confirm-value">${form.email || "—"}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Role</span>
              <span class="um-confirm-value um-role-badge ${form.role}">${form.role.replace("_", " ").toUpperCase()}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Department</span>
              <span class="um-confirm-value um-dept-badge">${form.department ? form.department.charAt(0).toUpperCase() + form.department.slice(1) : "—"}</span>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Create User",
      cancelButtonText: "Edit Details",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#9e9a93",
      customClass: { popup: "um-swal-popup" },
    });
    return result.isConfirmed;
  };

  const showUpdateConfirmation = async (user) => {
    const result = await Swal.fire({
      title: "Update User?",
      width: 480,
      html: `
        <div class="um-confirm-popup">
          <div class="um-confirm-header">
            <div class="um-confirm-icon update">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <p class="um-confirm-subtitle">Review changes for <b>@${user.username}</b></p>
          </div>
          <div class="um-confirm-details">
            <div class="um-confirm-row">
              <span class="um-confirm-label">Username</span>
              <span class="um-confirm-value">${form.username}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Full Name</span>
              <span class="um-confirm-value">${form.first_name} ${form.last_name}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Email</span>
              <span class="um-confirm-value">${form.email || "—"}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Role</span>
              <span class="um-confirm-value um-role-badge ${form.role}">${form.role.replace("_", " ").toUpperCase()}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Department</span>
              <span class="um-confirm-value um-dept-badge">${form.department ? form.department.charAt(0).toUpperCase() + form.department.slice(1) : "—"}</span>
            </div>
            ${form.password?.trim() ? '<div class="um-confirm-row highlight"><span class="um-confirm-label">Password</span><span class="um-confirm-value">Will be updated</span></div>' : ''}
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Update User",
      cancelButtonText: "Edit Details",
      confirmButtonColor: "#a9ae50",
      cancelButtonColor: "#9e9a93",
      customClass: { popup: "um-swal-popup" },
    });
    return result.isConfirmed;
  };

  const showDeleteConfirmation = async (user) => {
    const result = await Swal.fire({
      title: "Delete User?",
      width: 480,
      html: `
        <div class="um-confirm-popup">
          <div class="um-confirm-header">
            <div class="um-confirm-icon delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
            </div>
            <p class="um-confirm-subtitle">This will permanently remove the following user:</p>
          </div>
          <div class="um-confirm-details">
            <div class="um-confirm-row">
              <span class="um-confirm-label">Username</span>
              <span class="um-confirm-value">@${user.username}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Full Name</span>
              <span class="um-confirm-value">${user.first_name || ""} ${user.last_name || ""}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Role</span>
              <span class="um-confirm-value um-role-badge ${user.role}">${user.role?.replace("_", " ").toUpperCase() || "—"}</span>
            </div>
            <div class="um-confirm-row">
              <span class="um-confirm-label">Department</span>
              <span class="um-confirm-value um-dept-badge">${user.department ? user.department.charAt(0).toUpperCase() + user.department.slice(1) : "—"}</span>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#9e9a93",
      customClass: { popup: "um-swal-popup" },
    });
    return result.isConfirmed;
  };

  // ==========================================
  // FORM HANDLERS
  // ==========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.username || !form.role || !form.department) {
      Swal.fire("Error", "Username, Role and Department are required", "warning");
      return;
    }

    if (editingUser) {
      const confirmed = await showUpdateConfirmation(editingUser);
      if (!confirmed) return;
    } else {
      const confirmed = await showCreateConfirmation();
      if (!confirmed) return;
    }

    try {
      if (editingUser) {
        await updateUser(editingUser.id, buildPayload());
        await Swal.fire({
          title: "Updated!",
          text: "User updated successfully",
          icon: "success",
          confirmButtonColor: "#a9ae50",
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        await createUser(form);
        await Swal.fire({
          title: "Created!",
          text: "User added successfully",
          icon: "success",
          confirmButtonColor: "#a92f6f",
          timer: 2000,
          timerProgressBar: true,
        });
      }

      loadUsers();
      resetForm();
    } catch {
      Swal.fire("Error", "Operation failed", "error");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      role: user.role || "qc",
      department: user.department || "",
      password: "",
    });
    setIsFormVisible(true);
  };

  const handleDelete = async (user) => {
    const confirmed = await showDeleteConfirmation(user);
    if (!confirmed) return;

    try {
      await deleteUser(user.id);
      await Swal.fire({
        title: "Deleted!",
        text: "User removed successfully",
        icon: "success",
        confirmButtonColor: "#a92f6f",
        timer: 2000,
        timerProgressBar: true,
      });
      loadUsers();
    } catch {
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  const handleAddNew = () => {
    resetForm();
    setIsFormVisible(true);
  };

  // ==========================================
  // FILTERS
  // ==========================================

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      !searchTerm ||
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "all" || u.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  // ==========================================
  // ROLE HELPERS
  // ==========================================

  const getRoleConfig = (role) => {
    const configs = {
      admin: { label: "Admin", class: "admin", icon: "👑" },
      qc: { label: "QC", class: "qc", icon: "✓" },
      team_leader: { label: "Team Leader", class: "team", icon: "★" },
      field_manager: { label: "Field Manager", class: "manager", icon: "🌿" },
      packhouse_manager: { label: "Packhouse Manager", class: "packhouse", icon: "📦" },
      key_accounts_manager: { label: "Key Accounts Manager", class: "key-accounts", icon: "🌹" },
      customer: { label: "Customer", class: "customer", icon: "✿" },
    };
    return configs[role] || { label: role, class: "", icon: "●" };
  };

  const getInitials = (first, last) => {
    return `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";
  };

  const getAvatarColor = (username) => {
    const colors = ["#a92f6f", "#a9ae50", "#c44a87", "#8f943f", "#d97706", "#7c3aed"];
    let hash = 0;
    for (let i = 0; i < username?.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="um-page">
      {/* HEADER */}
      <header className="um-header">
        <div className="um-header-content">
          <div className="um-header-brand">
            <div className="um-header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <div>
              <span className="um-header-kicker">RVR · ADMINISTRATION</span>
              <h1>Admin centre</h1>
              <p>People, roles and system access at a glance</p>
            </div>
          </div>
          {adminTab === "users" && canManageUsers && <button className="um-btn-add" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Add User
          </button>}
        </div>
      </header>

      <div className="um-content-shell">
      <nav className="um-admin-nav" aria-label="Admin sections">
        {[['users', 'Users', '01'], ...(isAdmin ? [['access', 'Roles & rights', '02']] : [])].map(([key, label, number]) => <button key={key} className={`um-admin-tab${adminTab === key ? ' active' : ''}`} aria-current={adminTab === key ? 'page' : undefined} onClick={() => { setAdminTab(key); setSearchParams(key === 'access' ? { tab: 'access' } : {}); }}><span>{number}</span>{label}<b aria-hidden="true">↗</b></button>)}
      </nav>

      {adminTab === "access" ? <UserAccessControl /> : <>

      {/* STATS BAR */}
      <div className="um-stats-bar">
        <div className="um-stat-card total">
          <div className="um-stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
            </svg>
          </div>
          <div className="um-stat-info">
            <span className="um-stat-value">{users.length}</span>
            <span className="um-stat-label">Total Users</span>
          </div>
        </div>

        {["admin", "qc", "team_leader", "field_manager", "packhouse_manager", "key_accounts_manager"].map((role) => (
          <div key={role} className={`um-stat-card ${role}`}>
            <div className="um-stat-dot"></div>
            <div className="um-stat-info">
              <span className="um-stat-value">{roleCounts[role] || 0}</span>
              <span className="um-stat-label">{getRoleConfig(role).label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="um-main">
        {/* FORM PANEL */}
        {isFormVisible && (
          <div className="um-form-panel">
            <div className="um-form-header">
              <div className="um-form-title">
                <div className={`um-form-title-icon ${editingUser ? "edit" : "create"}`}>
                  {editingUser ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <path d="M20 8v6M23 11h-6"/>
                    </svg>
                  )}
                </div>
                <h3>{editingUser ? "Edit User" : "Create New User"}</h3>
              </div>
              <button className="um-form-close" onClick={resetForm}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form className="um-form" onSubmit={handleSubmit}>
              <div className="um-form-grid">
                <div className="um-form-field">
                  <label>Username <span className="required">*</span></label>
                  <input
                    name="username"
                    placeholder="Enter username"
                    value={form.username}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="um-form-field">
                  <label>First Name</label>
                  <input
                    name="first_name"
                    placeholder="Enter first name"
                    value={form.first_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="um-form-field">
                  <label>Last Name</label>
                  <input
                    name="last_name"
                    placeholder="Enter last name"
                    value={form.last_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="um-form-field">
                  <label>Email</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="user@example.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="um-form-field">
                  <label>Role <span className="required">*</span></label>
                  <select name="role" value={form.role} onChange={handleChange}>
                    <option value="qc">QC</option>
                    <option value="admin">Admin</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="field_manager">Field Manager</option>
                    <option value="packhouse_manager">Packhouse Manager</option>
                    <option value="key_accounts_manager">Key Accounts Manager</option>
                  </select>
                </div>

                <div className="um-form-field">
                  <label>Department <span className="required">*</span></label>
                  <select name="department" value={form.department} onChange={handleChange}>
                    <option value="">Select Department</option>
                    <option value="production">Production</option>
                    <option value="packhouse">Packhouse</option>
                    <option value="it">IT</option>
                  </select>
                </div>

                <div className="um-form-field full-width">
                  <label>
                    {editingUser ? "New Password (leave blank to keep current)" : "Password"}
                    {!editingUser && <span className="required">*</span>}
                  </label>
                  <input
                    name="password"
                    type="password"
                    placeholder={editingUser ? "••••••••" : "Create a password"}
                    value={form.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="um-form-actions">
                <button type="submit" className="um-btn-submit">
                  {editingUser ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 13l4 4L19 7"/>
                      </svg>
                      Update User
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                      Create User
                    </>
                  )}
                </button>
                <button type="button" className="um-btn-cancel" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TOOLBAR */}
        <div className="um-toolbar">
          <div className="um-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="um-filter">
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="qc">QC</option>
              <option value="team_leader">Team Leader</option>
              <option value="field_manager">Field Manager</option>
              <option value="packhouse_manager">Packhouse Manager</option>
                    <option value="key_accounts_manager">Key Accounts Manager</option>
            </select>
          </div>
        </div>

        {/* USER LIST */}
        <div className="um-users-section">
          <div className="um-section-header">
            <h3>All Users</h3>
            <span className="um-count">{filteredUsers.length} result{filteredUsers.length !== 1 ? "s" : ""}</span>
          </div>

          {/* DESKTOP TABLE */}
          <div className="um-table-wrapper">
            <table className="um-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const roleConfig = getRoleConfig(u.role);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="um-user-cell">
                          <div
                            className="um-avatar"
                            style={{ background: getAvatarColor(u.username) }}
                          >
                            {getInitials(u.first_name, u.last_name)}
                          </div>
                          <div className="um-user-info">
                            <span className="um-user-name">{u.first_name} {u.last_name}</span>
                            <span className="um-user-handle">@{u.username}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="um-user-email">{u.email || "—"}</span>
                      </td>
                      <td>
                        <span className={`um-role-pill ${roleConfig.class}`}>
                          <span className="um-role-icon">{roleConfig.icon}</span>
                          {roleConfig.label}
                        </span>
                      </td>
                      <td>
                        <span className="um-dept-pill">
                          {u.department ? u.department.charAt(0).toUpperCase() + u.department.slice(1) : "—"}
                        </span>
                      </td>
                      <td>
                        {canManageUsers && <div className="um-actions">
                          <button className="um-btn-edit" onClick={() => handleEdit(u)} title="Edit user">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="um-btn-delete" onClick={() => handleDelete(u)} title="Delete user">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS */}
          <div className="um-cards">
            {filteredUsers.map((u) => {
              const roleConfig = getRoleConfig(u.role);
              return (
                <div key={u.id} className="um-card">
                  <div className="um-card-header">
                    <div
                      className="um-avatar"
                      style={{ background: getAvatarColor(u.username) }}
                    >
                      {getInitials(u.first_name, u.last_name)}
                    </div>
                    <div className="um-card-info">
                      <span className="um-card-name">{u.first_name} {u.last_name}</span>
                      <span className="um-card-handle">@{u.username}</span>
                    </div>
                    <span className={`um-role-pill ${roleConfig.class}`}>
                      {roleConfig.label}
                    </span>
                  </div>
                  <div className="um-card-body">
                    <div className="um-card-row">
                      <span className="um-card-label">Email</span>
                      <span className="um-card-value">{u.email || "—"}</span>
                    </div>
                    <div className="um-card-row">
                      <span className="um-card-label">Department</span>
                      <span className="um-dept-pill">
                        {u.department ? u.department.charAt(0).toUpperCase() + u.department.slice(1) : "—"}
                      </span>
                    </div>
                  </div>
                  {canManageUsers && <div className="um-card-actions">
                    <button className="um-btn-edit" onClick={() => handleEdit(u)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit
                    </button>
                    <button className="um-btn-delete" onClick={() => handleDelete(u)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                      Delete
                    </button>
                  </div>}
                </div>
              );
            })}
          </div>

          {filteredUsers.length === 0 && (
            <div className="um-empty">
              <div className="um-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
              </div>
              <h4>No users found</h4>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
      </>}
      </div>
    </div>
  );
};

export default UserManagementPage;
